import { useCallback, useEffect, useMemo, useState } from 'react'
import type { ReactElement, ReactNode } from 'react'
import { I18nContext } from './context'
import type { I18nContextValue, Lang } from './context'
import no from './locales/no.json'
import en from './locales/en.json'

type Dict = Record<string, unknown>

const dictionaries: Record<Lang, Dict> = { no, en }

const STORAGE_KEY = 'sm-lang'

function resolveInitialLang(): Lang {
  if (typeof window === 'undefined') return 'no'
  const url = new URL(window.location.href)
  const param = url.searchParams.get('lang')
  if (param === 'no' || param === 'en') return param
  const stored = window.localStorage.getItem(STORAGE_KEY)
  if (stored === 'no' || stored === 'en') return stored
  return 'no'
}

function lookup(dict: Dict, key: string): string | undefined {
  const parts = key.split('.')
  let node: unknown = dict
  for (const part of parts) {
    if (node && typeof node === 'object' && part in (node as Dict)) {
      node = (node as Dict)[part]
    } else {
      return undefined
    }
  }
  return typeof node === 'string' ? node : undefined
}

function interpolate(template: string, vars?: Record<string, string | number>): string {
  if (!vars) return template
  return template.replace(/\{\{(\w+)\}\}/g, (_, name) => {
    const v = vars[name]
    return v === undefined ? `{{${name}}}` : String(v)
  })
}

export function I18nProvider({ children }: { children: ReactNode }): ReactElement {
  const [lang, setLangState] = useState<Lang>(resolveInitialLang)

  useEffect(() => {
    window.localStorage.setItem(STORAGE_KEY, lang)
    document.documentElement.lang = lang
  }, [lang])

  const setLang = useCallback((next: Lang) => setLangState(next), [])

  const t = useCallback(
    (key: string, vars?: Record<string, string | number>) => {
      const primary = lookup(dictionaries[lang], key)
      if (primary !== undefined) return interpolate(primary, vars)
      const fallback = lookup(dictionaries.en, key)
      if (fallback !== undefined) return interpolate(fallback, vars)
      return key
    },
    [lang]
  )

  const value = useMemo<I18nContextValue>(() => ({ lang, setLang, t }), [lang, setLang, t])

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>
}
